/**
 * The top-level route segments that are intentionally omitted from static generation. Keeping this
 * dependency-free lets the Vite build filter and the browser bootstrap share one route contract.
 *
 * The EDGE keeps its own copy of this list — `SPA_PREFIXES` in kitecorp/devops
 * `transferify/terraform/modules/cloudfront/route-shells.js` — and the two are hand-mirrored, so a
 * segment added here is not served as the app shell until that one is updated and applied. Until then a
 * direct hit or refresh on the new path still RENDERS (CloudFront's `custom_error_response` returns the
 * root `/index.html` body), but it does so with a 404 status. `personalization` is in exactly that state
 * pending kitecorp/devops#270; kitecorp/devops#271 is the guard that would have caught the drift.
 */
export const CLIENT_ONLY_ROUTE_SEGMENTS = [
	"activate",
	"account",
	"transfers",
	"contacts",
	"reminders",
	// ui#1292: the top bar's Personalization sheet, which owns a URL now.
	"personalization",
] as const;

/** The head bootstrap sets this marker before the body can paint on a client-only direct request. */
export const CLIENT_ONLY_ROUTE_SHELL_ATTRIBUTE = "data-client-only-route-shell";

interface ClientOnlyRouteShellHeadTag {
	tag: "script" | "style";
	attrs: {id: string};
	children: string;
	injectTo: "head-prepend";
}

/** True when a route belongs to one of the private, client-rendered route trees. */
export function isClientOnlyRoutePath(pathname: string): boolean {
	const segment = pathname.replace(/^\/+/, "").split("/")[0];
	return CLIENT_ONLY_ROUTE_SEGMENTS.some(clientOnlySegment => clientOnlySegment === segment);
}

/**
 * Head tags that hide a private fallback root before first paint. The inline bootstrap is generated
 * from {@link CLIENT_ONLY_ROUTE_SEGMENTS}, the same route source the SSG filter and main callback use;
 * it contains no independently-maintained route pattern.
 */
export function clientOnlyRouteShellHeadTags(): ClientOnlyRouteShellHeadTag[] {
	const segments = JSON.stringify(CLIENT_ONLY_ROUTE_SEGMENTS);
	const marker = JSON.stringify(CLIENT_ONLY_ROUTE_SHELL_ATTRIBUTE);
	return [
		{
			tag: "style",
			attrs: {id: "client-only-route-shell-style"},
			children: `html[${CLIENT_ONLY_ROUTE_SHELL_ATTRIBUTE}] #root{visibility:hidden!important}`,
			injectTo: "head-prepend",
		},
		{
			tag: "script",
			attrs: {id: "client-only-route-shell-bootstrap"},
			children: `(()=>{const segments=${segments};const segment=window.location.pathname.split("/")[1]??"";if(segments.includes(segment)){document.documentElement.setAttribute(${marker},"");}})();`,
			injectTo: "head-prepend",
		},
	];
}

/**
 * Converts the landing-page fallback document into the empty root expected by a client render.
 * Public SSG documents remain untouched and continue through vite-react-ssg's hydration path.
 */
export function prepareClientOnlyRouteShell(
	pathname: string,
	root: Element | null,
	documentRoot: Element | null = null,
): boolean {
	if (root === null || !isClientOnlyRoutePath(pathname)) {
		return false;
	}
	root.removeAttribute("data-server-rendered");
	root.replaceChildren();
	// Reveal only after the unrelated landing markup and hydration marker are both gone. React then
	// mounts the requested lazy route into this neutral, empty root through createRoot.
	documentRoot?.removeAttribute(CLIENT_ONLY_ROUTE_SHELL_ATTRIBUTE);
	return true;
}
